import type { AxiosAdapter } from "axios";
import { demoUser } from "./demoMode.ts";

const demoDate = (daysAgo: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString();
};

const demoRequests = [
  {
    id: "demo-request-1",
    user_id: demoUser.id,
    company_name: "Демо-проект «Север»",
    client_name: "Демо-клиент",
    client_contact: demoUser.email,
    status: "in_progress",
    tariff: "standard",
    workflow_stage: "design",
    created_at: demoDate(2),
    updated_at: demoDate(0),
  },
  {
    id: "demo-request-2",
    user_id: demoUser.id,
    company_name: "Тестовый каталог",
    client_name: "Команда демо",
    client_contact: demoUser.email,
    status: "success",
    tariff: "premium",
    workflow_stage: "delivered",
    created_at: demoDate(9),
    updated_at: demoDate(3),
  },
];

const demoSites = [
  {
    id: "demo-site-1",
    request_id: "demo-request-1",
    manager_id: demoUser.id,
    company_name: "Демо-проект «Север»",
    client_name: "Демо-клиент",
    client_contact: demoUser.email,
    domain: "demo.localhost",
    domain_status: "connected",
    ssl_enabled: true,
    preview_url: "http://localhost:3000",
    deploy_status: "active",
    generation_status: "completed",
    hosting_plan: "demo",
    site_type: "landing",
    tariff: "standard",
    created_at: demoDate(5),
    updated_at: demoDate(1),
  },
];

const demoBillingSiteItem = {
  site_id: "demo-site-1",
  owner_auth_user_id: demoUser.id,
  plan_code: "studio_lite_monthly",
  subscription_status: "active",
  maintenance_status: "active",
  billing_interval: "monthly",
  active_seats: 3,
  extra_seats: 0,
  manual_pricing_required: false,
  current_period_end: demoDate(-25),
  maintenance_period_end: demoDate(-25),
  first_year_support_until: demoDate(-180),
  last_invoice_id: "demo-invoice-1",
  last_invoice_status: "open",
  last_invoice_subject_type: "subscription",
  last_invoice_total_minor: 99000,
  last_invoice_due_at: demoDate(-7),
  last_payment_status: "paid",
  open_invoices: 1,
  failed_payments: 0,
  operational_status: "active",
  flags: [],
  updated_at: demoDate(0),
  site: demoSites[0],
};

const demoBillingSummary = {
  site_id: "demo-site-1",
  subscription: {
    id: "demo-subscription-1",
    owner_auth_user_id: demoUser.id,
    plan_code: "studio_lite_monthly",
    status: "active",
    billing_interval: "monthly",
    currency: "RUB",
    included_seats: 5,
    active_seats: 3,
    extra_seats: 0,
    manual_pricing_required: false,
    cancel_at_period_end: false,
    billing_anchor: demoDate(5).slice(0, 10),
    current_period_start: demoDate(5),
    current_period_end: demoDate(-25),
    created_at: demoDate(30),
    updated_at: demoDate(0),
  },
  maintenance: {
    id: "demo-maintenance-1",
    status: "active",
    period_start: demoDate(5),
    period_end: demoDate(-25),
    auto_renew: true,
    created_at: demoDate(30),
    updated_at: demoDate(0),
  },
  lifecycle: {
    accepted_at: demoDate(30),
    first_year_support_until: demoDate(-180),
  },
  entitlements: {
    site_editor: true,
    site_publish: true,
    analytics_basic: true,
    intake_basic: true,
    crm_full: true,
    staff_access: true,
    tracker_access: true,
    omnichannel_access: true,
    messaging_access: true,
    notifications_access: true,
    notifications_team_access: true,
    public_site_hosting: true,
  },
  maintenance_only: false,
  payment_methods: [],
  site: demoSites[0],
};

const demoBillingDetail = {
  site_id: "demo-site-1",
  summary: demoBillingSummary,
  invoices: [
    {
      id: "demo-invoice-1",
      number: "DEMO-0001",
      site_id: "demo-site-1",
      subject_type: "subscription",
      plan_code: "studio_lite_monthly",
      status: "open",
      currency: "RUB",
      subtotal_minor: 99000,
      discount_minor: 0,
      tax_minor: 0,
      total_minor: 99000,
      due_at: demoDate(-7),
      period_start: demoDate(5),
      period_end: demoDate(-25),
      payment_channel: "bank_transfer",
      created_at: demoDate(7),
      updated_at: demoDate(7),
    },
  ],
  payments: [],
  adjustments: [],
  dunning_runs: [],
  manual_reviews: [],
  billing_profile: {
    site_id: "demo-site-1",
    owner_auth_user_id: demoUser.id,
    payer_type: "company",
    display_name: "Демо-проект «Север»",
    legal_name: "Демо-проект «Север»",
    email: demoUser.email,
  },
  audit_logs: [],
  flags: [],
  generated_at: demoDate(0),
  site: demoSites[0],
};

const demoBillingOperations = {
  redis_available: true,
  operational_degraded: false,
  overdue_open_invoices: 1,
  payment_failed_invoices: 0,
  failed_payments_24h: 0,
  stuck_dunning_runs: 0,
  failed_lifecycle_outbox: 0,
  stuck_lifecycle_outbox: 0,
  stuck_notification_outbox: 0,
  failed_notification_outbox: 0,
  purge_due_subjects: 0,
  unprocessed_provider_events: 0,
  duplicate_provider_events: 0,
  orphaned_entitlement_sites: 0,
  pending_manual_reviews: 0,
  past_due_subscriptions: 0,
  alerts: [],
  recent_provider_events: [],
  generated_at: demoDate(0),
};

const demoClients = [
  {
    id: "demo-client-1",
    site_id: "demo-site-1",
    auth_user_id: "demo-client-user",
    auth_source: "legacy",
    identity_status: "provisioned",
    cabinet_status: "active",
    company_name: "Демо-проект «Север»",
    client_name: "Демо-клиент",
    email: "client@local.test",
    client_contact: "client@local.test",
    created_at: demoDate(4),
    site: {
      id: "demo-site-1",
      domain: "demo.localhost",
      deploy_status: "active",
    },
  },
];

const demoFeedback = [
  {
    id: "demo-feedback-1",
    user_id: demoUser.id,
    subject: "Демо-обращение",
    message: "Локальное обращение для проверки интерфейса.",
    status: "new",
    category: "other",
    priority: "normal",
    created_at: demoDate(1),
    updated_at: demoDate(1),
    user_email: demoUser.email,
    user_first_name: demoUser.first_name,
    user_last_name: demoUser.last_name,
  },
];

const demoTickets = [
  {
    id: "demo-ticket-1",
    title: "Проверка карточки поддержки",
    description: "Локальный тикет для просмотра рабочего сценария.",
    status: "in_progress",
    user_id: demoUser.id,
    user_name: demoUser.full_name,
    user_email: demoUser.email,
    company_name: "Демо-проект «Север»",
    assignment_status: "accepted",
    assignment_attempts: 1,
    assigned_manager_id: demoUser.id,
    assigned_manager_name: demoUser.full_name,
    created_at: demoDate(3),
    updated_at: demoDate(1),
  },
];

const demoManager = {
  id: "demo-manager-1",
  email: "manager@local.test",
  first_name: "Демо",
  last_name: "Менеджер",
  role: "manager",
  is_blocked: false,
  status: "active",
  approval_status: "approved",
  created_at: demoDate(30),
  request_count: 8,
  site_count: 3,
};

const demoTimeseries = Array.from({ length: 6 }, (_, index) => ({
  period: demoDate(25 - index * 5).slice(0, 10),
  value: 4 + index * 2,
  value2: 2 + index,
}));

const demoOwnerStats = {
  requests_last_month: 18,
  requests_prev_month: 14,
  requests_change_percent: 28.6,
  active_sites: 7,
  active_sites_last_month: 5,
  active_sites_prev_month: 4,
  active_sites_change_percent: 40,
  active_users_week: 11,
  active_users_prev_week: 8,
  active_users_change_percent: 37.5,
  total_active_users: 24,
  total_users: 31,
  pending_requests: 4,
  completed_requests: 14,
  pending_approvals: 2,
};

const demoDashboardStats = {
  total_users: 31,
  active_users: 24,
  total_requests: 18,
  pending_requests: 4,
  completed_requests: 14,
  total_sites: 9,
  active_sites: 7,
  total_revenue: 482000,
  monthly_revenue: 126000,
  pending_approvals: 2,
};

const listResponse = <T>(items: T[], limit = 50) => ({
  items,
  page: 1,
  limit,
  total: items.length,
});

function resolveRequestUrl(config: Parameters<AxiosAdapter>[0]): URL {
  const rawUrl = config.url || "/";
  const origin = typeof window === "undefined" ? "http://localhost:3000" : window.location.origin;

  if (/^https?:\/\//i.test(rawUrl)) return new URL(rawUrl);

  const baseUrl = new URL(config.baseURL || origin, origin);
  const basePath = baseUrl.pathname.replace(/\/$/, "");
  const requestPath = rawUrl.replace(/^\//, "");
  return new URL(`${basePath}/${requestPath}`, baseUrl.origin);
}

function getApiPath(url: URL): string {
  const apiPrefix = url.pathname.match(/\/api\/v1(\/.*)?$/);
  return apiPrefix?.[1] || url.pathname;
}

function getRequest(id: string) {
  return demoRequests.find((request) => request.id === id) || demoRequests[0];
}

function getSite(id: string) {
  return demoSites.find((site) => site.id === id) || demoSites[0];
}

function responseFor(path: string, method: string): unknown {
  if (method === "GET") {
    if (path === "/users/me") return demoUser;
    if (path === "/users/me/stats") {
      return {
        total_requests: 18,
        completed_requests: 14,
        pending_requests: 4,
        this_week: 6,
        site_count: 9,
        active_site_count: 7,
        total_sites: 9,
        active_sites: 7,
      };
    }
    if (path === "/users/me/permissions") return { permissions: ["demo"] };

    if (path === "/admin/dashboard") return demoDashboardStats;
    if (path === "/owner/stats") return demoOwnerStats;
    if (path.startsWith("/owner/timeseries/")) return { data: demoTimeseries };
    if (path === "/owner/distribution/requests") {
      return { data: [{ status: "in_progress", count: 4 }, { status: "success", count: 14 }] };
    }
    if (path === "/owner/distribution/sites") {
      return { data: [{ status: "active", count: 7 }, { status: "pending", count: 2 }] };
    }
    if (path === "/owner/distribution/users") {
      return { data: [{ status: "active", count: 24 }, { status: "pending", count: 7 }] };
    }
    if (path.startsWith("/owner/top-managers")) return { data: [{ ...demoManager, completed_requests: 6 }] };
    if (path.startsWith("/owner/recent-activity")) {
      return {
        data: [
          {
            activity_type: "request",
            entity_id: demoRequests[0].id,
            title: demoRequests[0].company_name,
            status: demoRequests[0].status,
            timestamp: demoDate(0),
            user_email: demoUser.email,
            user_first_name: demoUser.first_name,
            user_last_name: demoUser.last_name,
          },
        ],
      };
    }

    if (path === "/requests" || path.startsWith("/requests/search")) return listResponse(demoRequests);
    if (/^\/requests\/[^/]+$/.test(path)) return getRequest(path.split("/")[2]);
    if (path === "/sites" || path === "/sites/expiring") return path === "/sites" ? listResponse(demoSites) : demoSites;
    if (path === "/sites/stats") return { total_sites: 1, active_sites: 1, pending_sites: 0, failed_sites: 0, trial_sites: 1, paid_sites: 0 };
    if (/^\/sites\/[^/]+$/.test(path)) return getSite(path.split("/")[2]);
    if (path.startsWith("/clients")) {
      if (/^\/clients\/site\/[^/]+$/.test(path)) return demoClients[0];
      return listResponse(demoClients);
    }
    if (path === "/feedback" || path === "/admin/feedback") return listResponse(demoFeedback);
    if (/^\/(feedback|admin\/feedback)\/[^/]+$/.test(path)) return demoFeedback[0];
    if (path.endsWith("/messages")) return [];
    if (path.startsWith("/support/tickets")) {
      if (/^\/support\/tickets\/[^/]+$/.test(path)) return demoTickets[0];
      if (path.endsWith("/attachments")) return [];
      return demoTickets;
    }
    if (path.startsWith("/admin/managers") || path === "/users/managers") return { items: [demoManager] };
    if (path === "/admin/users" || path === "/users/admins" || path === "/users/pending") return { items: [demoManager] };
    if (path === "/admin/groups") return { items: [{ id: "demo-group", name: "Демо-группа", member_count: 1, max_members: 10, current_members: 1, available_slots: 9 }] };
    if (path === "/admin/groups/capacity") return { groups: [] };
    if (path === "/admin/billing/sites") {
      return {
        items: [demoBillingSiteItem],
        stats: {
          total_sites: 1,
          active_subscriptions: 1,
          past_due_subscriptions: 0,
          suspended_subscriptions: 0,
          purged_sites: 0,
          maintenance_active: 1,
          manual_reviews_pending: 0,
          dunning_scheduled: 0,
          payment_failed_invoices: 0,
        },
        total: 1,
        limit: 100,
      };
    }
    if (/^\/admin\/billing\/sites\/[^/]+$/.test(path)) return demoBillingDetail;
    if (path === "/admin/billing/operations") return demoBillingOperations;
    if (path.startsWith("/admin/billing/bank-operations")) return { items: [], page: 1, limit: 50, total: 0 };
    if (path.startsWith("/admin/billing/lifecycle-outbox")) return { items: [], page: 1, limit: 50, total: 0 };
    if (path.startsWith("/admin/billing/offerings")) return { items: [], total: 0 };
    if (path.startsWith("/notifications")) return { success: true, data: { notifications: [], count: 0 } };
    if (path.startsWith("/gamification/")) return { items: [], data: [] };
    if (path === "/additional-services") return [];
    if (path.startsWith("/leads") || path.startsWith("/supervisor/")) return { success: true, data: { leads: [], escalations: [], managers: [] } };
    if (path.startsWith("/wenlix-site-analytics/")) return { items: [], data: [], total: 0, page: 1, limit: 50 };
    if (path.startsWith("/site-deletion-requests")) return { items: [], page: 1, limit: 50, total: 0 };

    return { success: true, items: [], data: [] };
  }

  if (method === "POST" && path === "/requests") return demoRequests[0];
  if (method === "POST" && path === "/sites") return demoSites[0];
  if (/^\/requests\/[^/]+/.test(path)) return getRequest(path.split("/")[2]);
  if (/^\/sites\/[^/]+/.test(path)) return getSite(path.split("/")[2]);
  if (path.startsWith("/domains/") && path.endsWith("/check")) {
    return { status: "available", domain: "demo.localhost", alternatives: [], message: "Демо-проверка домена" };
  }
  if (path.startsWith("/owner/deploy")) return { success: true, deploy_id: "demo-deploy", status: "completed" };
  if (path.startsWith("/reviews/")) return { reviews: [], source: "demo" };

  return { success: true, message: "Демо: изменение применено только в памяти браузера." };
}

/** Axios adapter used only when VITE_DEMO_MODE=1 is active in dev. */
export const demoAxiosAdapter: AxiosAdapter = async (config) => {
  if (config.signal?.aborted) {
    throw new DOMException("The operation was aborted", "AbortError");
  }

  const url = resolveRequestUrl(config);
  const path = getApiPath(url);
  const method = (config.method || "get").toUpperCase();
  const data = config.responseType === "blob"
    ? new Blob([JSON.stringify({ demo: true })], { type: "application/json" })
    : responseFor(path, method);

  return {
    data,
    status: 200,
    statusText: "OK",
    headers: { "content-type": "application/json" },
    config,
  };
};
